/**
 * Acquisition parameter plumbing — the website's half of the ad→install
 * attribution chain.
 *
 * An ad lands a visitor here with utm/click params on the URL. Those are
 * captured once into localStorage so they survive normal browsing, then
 * re-attached when the visitor finally clicks a store CTA — which points at
 * our own /ios and /android redirects (CloudFront function, see
 * infrastructure/WebsiteStage.ts) rather than the stores directly. The
 * redirect logs the click and forwards the params into each store's
 * attribution channel.
 */

const STORAGE_KEY = 'ra-acquisition';

/** Last-touch attribution: a fresh campaign visit overwrites an older one. */
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

/** The params worth carrying across the funnel — mirrors the redirect's list. */
const PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'fbclid'
] as const;

interface StoredAcquisition {
  params: Record<string, string>;
  at: number;
}

const readStored = (): StoredAcquisition | null => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as StoredAcquisition;
    if (!stored?.params || Date.now() - stored.at > TTL_MS) return null;
    return stored;
  } catch {
    // Private windows / blocked storage — attribution is best-effort.
    return null;
  }
};

/**
 * Capture campaign params from the landing URL. Call once at boot, before
 * render. Only overwrites a previous capture when the current URL actually
 * carries params (last-touch), so in-site navigation doesn't erase them.
 */
export const captureAcquisitionParams = (): void => {
  if (typeof window === 'undefined') return;
  try {
    const search = new URLSearchParams(window.location.search);
    const params: Record<string, string> = {};
    for (const key of PARAM_KEYS) {
      const value = search.get(key);
      if (value) params[key] = value;
    }
    if (Object.keys(params).length === 0) return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ params, at: Date.now() }));
  } catch {
    /* best-effort */
  }
};

/**
 * Store CTA href — always the same-origin redirect (so the click is logged
 * and params ride into the store URL), with any captured campaign params
 * re-attached. With nothing captured this is just '/ios' or '/android'.
 */
export const storeHref = (platform: 'ios' | 'android'): string => {
  const path = `/${platform}`;
  if (typeof window === 'undefined') return path;
  const stored = readStored();
  if (!stored) return path;
  const search = new URLSearchParams(stored.params);
  const query = search.toString();
  return query ? `${path}?${query}` : path;
};

/**
 * The captured campaign params (last touch, 30-day window), for beacons that
 * want to attribute an event back to the landing. Null when nothing was captured.
 */
export const readAcquisitionParams = (): Record<string, string> | null =>
  readStored()?.params ?? null;
